import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../../../error.utils';
import { upsertQueryParams } from '../../../../../helpers';
import { mapTimezoneToIana } from '../../../../../date.utils';
import { AssignmentService } from '../../../../../service/assignment.service';
import { BranchService } from '../../../../../service/branch.service';
import { LossPreventionService } from '../../../../../service/loss-prevention.service';
import { NotifyService } from '../../../../../service/notify.service';
import { WalkoutService } from '../../../../../service/walkout.service';
import { ReviewSubjectType } from '../../../../../types';
import { Branch } from '../../../../../types/branch';
import { Kiosk } from '../../../../../types/impl';
import { LocationTiny } from '../../../../../types/location';
import { CreateWalkoutRequest } from '../../../../../types/transaction';
import { generateUuid as generateUuidUtil } from '../../../../../utils';
import { DateTime } from 'luxon';
import { PageSelectLocationComponent } from '../../../../components/page-select-location/page-select-location.component';
import { LocationService } from '../../../../../service/location.service';

type SimpleItem = { description: string; price?: number | string };

@Component({
  selector: 'app-create-walkout-review',
  templateUrl: './create-walkout.component.html',
  styleUrl: './create-walkout.component.scss',
  standalone: false,
})
export class CreateWalkoutComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private assignmentService = inject(AssignmentService);
  private lpService = inject(LossPreventionService);
  private walkoutService = inject(WalkoutService);
  private notify = inject(NotifyService);
  private branchService = inject(BranchService);
  private locationService = inject(LocationService);
  private locationSelect = viewChild<PageSelectLocationComponent>('locationSelect');

  public branch = signal<Branch | undefined>(undefined);
  public location = signal<LocationTiny | undefined>(undefined);
  public locationTimezone = signal<string | undefined>(undefined);
  public locationTimezoneDisplay = computed(() => {
    const timezone = this.locationTimezone();
    if (!timezone) return undefined;
    const resolvedZone = mapTimezoneToIana(timezone) ?? timezone;
    return `${timezone} (${DateTime.now().setZone(resolvedZone).toFormat('ZZZZ')})`;
  });

  public branchAlias = signal<string>('');
  public locationAlias = signal<string>('');
  public selectedBranchAliasId = signal<number | undefined>(undefined);
  public selectedKioskId = signal<number | undefined>(undefined);
  public uniqueId = signal<string>('');
  public kiosks = signal<Kiosk[]>([]);
  public branchAliasOptions = signal<Array<{ id: number; name: string }>>([]);
  public locationAliasOptions = signal<
    Array<{
      id: number;
      name: string;
      branch_alias_id: number;
      branch_alias_name: string;
    }>
  >([]);

  public isLoadingBranchAlias = signal<boolean>(false);
  public isLoadingLocationAlias = signal<boolean>(false);
  private branchIdParam = signal<number | undefined>(undefined);
  private locationIdParam = signal<number | undefined>(undefined);
  private branchAliasIdParam = signal<number | undefined>(undefined);
  private locationAliasIdParam = signal<number | undefined>(undefined);
  private hasAppliedQueryParams = signal<boolean>(false);
  public autoSelectBranchIds = computed(() => (this.branchIdParam() ? [this.branchIdParam() as number] : []));
  public autoSelectLocationIds = computed(() => (this.locationIdParam() ? [this.locationIdParam() as number] : []));
  private lastBranchId = signal<number | undefined>(undefined);
  private lastLocationId = signal<number | undefined>(undefined);
  private lastBranchAliasId = signal<number | undefined>(undefined);
  private lastKioskId = signal<number | undefined>(undefined);

  private shouldPreserveAliasParamsOnInitForBranch(branchId: number) {
    return (
      !this.hasAppliedQueryParams() &&
      this.branchIdParam() === branchId &&
      (this.branchAliasIdParam() !== undefined || this.locationAliasIdParam() !== undefined)
    );
  }

  private shouldPreserveAliasParamsOnInitForLocation(locationId: number) {
    return (
      !this.hasAppliedQueryParams() &&
      this.locationIdParam() === locationId &&
      (this.branchAliasIdParam() !== undefined || this.locationAliasIdParam() !== undefined)
    );
  }

  public walkoutDateDate = signal<Date>(new Date());
  public walkoutTime = signal<string>(this.formatTime(new Date()));
  public theftScore = signal<number>(1);
  public isCreatingWalkout = signal<boolean>(false);
  public isCreatingReview = signal<boolean>(false);

  // Walkout specifics
  public walkoutStatus = signal<string>('Review Theft');
  public matchingTransaction = signal<boolean>(false);
  public cameraUuid = signal<string | null>(null);

  // Items
  public transactionItems = signal<SimpleItem[]>([]);

  // Image upload rows
  public photoRows = signal<Array<{ file: File | null; previewUrl: string | null }>>([]);

  // Video upload (single file)
  public videoFile = signal<File | null>(null);
  public videoOffsetSeconds = signal<number | undefined>(undefined);
  public videoDurationSeconds = signal<number | undefined>(undefined);

  public branchId = computed(() => this.branch()?.id);
  public locationId = computed(() => this.location()?.id);
  public createdWalkoutId = signal<string | undefined>(undefined);
  public createdReviewId = signal<string | undefined>(undefined);
  public canCreateWalkout = computed(
    () => !!this.branchId() && !!this.locationId() && !!this.locationAlias().trim() && !!this.uniqueId().trim(),
  );
  public canCreateReview = computed(
    () => this.createdWalkoutId() !== undefined && !!this.locationId() && this.createdReviewId() === undefined,
  );

  // Alias selection handlers (copy of cancellation form)
  public onBranchChange(branch: Branch[] | Branch | null) {
    if (!branch) {
      if (this.branch()) {
        this.branch.set(undefined);
        this.location.set(undefined);
        this.resetAliasSelections();
        this.clearAliasOptions();
        this.lastBranchId.set(undefined);
        this.lastLocationId.set(undefined);
        this.lastBranchAliasId.set(undefined);
        this.lastKioskId.set(undefined);
        upsertQueryParams(this.router, this.route, {
          branch_id: null,
          location_id: null,
          branch_alias_id: null,
          location_alias_id: null,
        });
        const locSelect = this.locationSelect();
        if (locSelect) locSelect.reset();
        this.createdWalkoutId.set(undefined);
        this.createdReviewId.set(undefined);
      }
      return;
    }
    const b = branch as Branch;
    const changed = this.lastBranchId() !== b.id;
    this.branch.set(b);
    if (changed) {
      this.lastBranchId.set(b.id);
      this.location.set(undefined);
      this.resetAliasSelections();
      this.lastLocationId.set(undefined);
      this.lastBranchAliasId.set(undefined);
      this.lastKioskId.set(undefined);
      const locSelect = this.locationSelect();
      if (locSelect) {
        locSelect.reset();
        locSelect.getLocations(this.branchId()!);
      }
      this.createdWalkoutId.set(undefined);
      this.createdReviewId.set(undefined);
      const preserveAliases = this.shouldPreserveAliasParamsOnInitForBranch(b.id);
      const preserveLocation = !this.hasAppliedQueryParams() && this.branchIdParam() === b.id && this.locationIdParam() !== undefined;
      const qp: { [k: string]: string | number | null | undefined } = { branch_id: b.id };
      if (!preserveLocation) qp.location_id = null;
      if (!preserveAliases) {
        qp.branch_alias_id = null;
        qp.location_alias_id = null;
      }
      upsertQueryParams(this.router, this.route, qp);
    }
    this.loadBranchLocationDetails();
  }

  public onLocationChange(location: LocationTiny[] | LocationTiny | null) {
    if (!location) {
      if (this.location()) {
        this.location.set(undefined);
        this.locationTimezone.set(undefined);
        this.resetAliasSelections();
        this.lastLocationId.set(undefined);
        this.lastBranchAliasId.set(undefined);
        this.lastKioskId.set(undefined);
        upsertQueryParams(this.router, this.route, {
          location_id: null,
          branch_alias_id: null,
          location_alias_id: null,
        });
        this.createdWalkoutId.set(undefined);
        this.createdReviewId.set(undefined);
      }
      return;
    }
    const loc = location as LocationTiny;
    const changed = this.lastLocationId() !== loc.id;
    this.location.set(loc);
    if (changed) {
      this.locationTimezone.set(undefined);
    }
    if (changed) {
      this.lastLocationId.set(loc.id);
      this.resetAliasSelections();
      this.lastBranchAliasId.set(undefined);
      this.lastKioskId.set(undefined);
      this.createdWalkoutId.set(undefined);
      this.createdReviewId.set(undefined);
      const preserveAliases = this.shouldPreserveAliasParamsOnInitForLocation(loc.id);
      const qp: { [k: string]: string | number | null | undefined } = { location_id: loc.id };
      if (!preserveAliases) {
        qp.branch_alias_id = null;
        qp.location_alias_id = null;
      }
      upsertQueryParams(this.router, this.route, qp);
    }

    this.loadBranchLocationDetails();
  }

  public onBranchAliasChange(branchAliasId?: number) {
    const nextId = branchAliasId ?? undefined;
    const changed = this.lastBranchAliasId() !== nextId;
    this.selectedBranchAliasId.set(nextId);
    const selected = this.branchAliasOptions().find((o) => o.id === branchAliasId);
    this.branchAlias.set(selected ? selected.name : '');
    if (changed) {
      this.lastBranchAliasId.set(nextId);
      this.selectedKioskId.set(undefined);
      this.lastKioskId.set(undefined);
      this.locationAlias.set('');
      this.refreshKioskOptions();
      this.createdWalkoutId.set(undefined);
      this.createdReviewId.set(undefined);
      const preserveLocationAlias =
        !this.hasAppliedQueryParams() && this.branchAliasIdParam() === nextId && this.locationAliasIdParam() !== undefined;
      const qp: { [k: string]: string | number | null | undefined } = { branch_alias_id: branchAliasId ?? null };
      if (!preserveLocationAlias) {
        qp.location_alias_id = null;
      }
      upsertQueryParams(this.router, this.route, qp);
    } else {
      this.refreshKioskOptions();
    }
  }

  public onKioskChange(kioskId?: number) {
    const nextId = kioskId ?? undefined;
    const changed = this.lastKioskId() !== nextId;
    this.selectedKioskId.set(nextId);
    const selected = this.locationAliasOptions().find((o) => o.id === kioskId);
    this.locationAlias.set(selected ? selected.name : '');
    if (changed) {
      this.lastKioskId.set(nextId);
      upsertQueryParams(this.router, this.route, { location_alias_id: kioskId ?? null });
      this.createdWalkoutId.set(undefined);
      this.createdReviewId.set(undefined);
    }
  }

  private refreshKioskOptions() {
    const aliasId = this.selectedBranchAliasId();
    const kiosks = this.kiosks();
    const filtered = aliasId ? kiosks.filter((k) => k.branch_alias_id === aliasId) : kiosks;
    const opts = filtered.map((k) => ({
      id: k.id,
      name: k.name,
      branch_alias_id: k.branch_alias_id,
      branch_alias_name: k.branch_alias_name,
    }));
    this.locationAliasOptions.set(opts);
  }

  private resetAliasSelections() {
    this.branchAlias.set('');
    this.locationAlias.set('');
    this.selectedBranchAliasId.set(undefined);
    this.selectedKioskId.set(undefined);
    this.clearAliasOptions();
  }

  private clearAliasOptions() {
    this.branchAliasOptions.set([]);
    this.locationAliasOptions.set([]);
    this.isLoadingBranchAlias.set(false);
    this.isLoadingLocationAlias.set(false);
  }

  private loadBranchLocationDetails() {
    const branchId = this.branchId();
    const locationId = this.locationId();
    if (!branchId || !locationId) {
      this.kiosks.set([]);
      this.locationTimezone.set(undefined);
      this.clearAliasOptions();
      this.createdWalkoutId.set(undefined);
      this.createdReviewId.set(undefined);
      return;
    }
    this.locationService.getLocation({ locationId: locationId, options: { include_kiosks: true } }).subscribe({
      next: (resp) => {
        const kiosks = resp.kiosks ?? [];
        this.kiosks.set(kiosks);
        this.locationTimezone.set(resp.timezone ?? undefined);

        const branchAliasMap = new Map<number, string>();
        for (const k of kiosks) {
          if (k.branch_alias_id && !branchAliasMap.has(k.branch_alias_id)) {
            branchAliasMap.set(k.branch_alias_id, k.branch_alias_name);
          }
        }
        const branchAliasOptions = Array.from(branchAliasMap.entries()).map(([id, name]) => ({ id, name }));
        this.branchAliasOptions.set(branchAliasOptions);
        this.refreshKioskOptions();
        if (!this.hasAppliedQueryParams()) {
          const desiredBranchAliasId = this.branchAliasIdParam();
          if (desiredBranchAliasId) {
            this.onBranchAliasChange(desiredBranchAliasId);
          }
          const desiredKioskId = this.locationAliasIdParam();
          if (desiredKioskId) {
            this.onKioskChange(desiredKioskId);
          }
          this.hasAppliedQueryParams.set(true);
        }
      },
      error: () => {
        this.kiosks.set([]);
        this.locationTimezone.set(undefined);
        this.clearAliasOptions();
      },
    });
  }

  // Items handlers
  public addTxnItem() {
    this.transactionItems.set([...this.transactionItems(), { description: '', price: undefined }]);
  }

  public removeTxnItem(index: number) {
    const next = [...this.transactionItems()];
    next.splice(index, 1);
    this.transactionItems.set(next);
  }

  public async onPhotoSelected($event: Event, index: number) {
    const input = $event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    const file = files?.[0] ?? null;
    if (!file) return;
    const previewUrl = await this.fileToDataUrl(file).catch(() => null);
    const next = [...this.photoRows()];
    next[index] = { file, previewUrl };
    this.photoRows.set(next);
  }

  public onVideoSelected($event: Event) {
    const input = $event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    const file = files?.[0] ?? null;
    this.videoFile.set(file);
  }

  public createWalkout() {
    if (!this.canCreateWalkout() || this.isCreatingWalkout()) return;
    const timezone = mapTimezoneToIana(this.locationTimezone()) ?? 'UTC';
    const sourceDate = this.walkoutDateDate() ?? new Date();
    const [hours, minutes, seconds] = this.parseTime(this.walkoutTime());
    const walkoutDateTime = DateTime.fromObject(
      {
        year: sourceDate.getFullYear(),
        month: sourceDate.getMonth() + 1,
        day: sourceDate.getDate(),
        hour: hours,
        minute: minutes,
        second: seconds ?? 0,
      },
      { zone: timezone },
    );
    let walkoutDateIso = walkoutDateTime.toISO();
    if (!walkoutDateIso) {
      const fallback = DateTime.now().setZone(timezone).toISO();
      walkoutDateIso = fallback ?? new Date().toISOString();
    }
    const uniqueId = this.uniqueId().trim();
    const imageFiles = this.getSelectedPhotoFiles();
    const video = this.videoFile();
    const videoOffset = this.videoOffsetSeconds();
    const videoDuration = this.videoDurationSeconds();

    const transItemsStr = this.serializeSimpleItemList(this.transactionItems());

    this.isCreatingWalkout.set(true);
    (imageFiles.length > 0 ? this.lpService.uploadFiles(imageFiles, uniqueId + '.jpg') : of<string[]>([]))
      .pipe(
        switchMap(() => (video ? this.lpService.uploadFiles([video], uniqueId + '.mp4') : of<string[]>([]))),
        switchMap((uploadedVideoLinks: string[]) => {
          const branch = this.branch()!;
          const location = this.location()!;
          const branchAliasName = this.branchAlias().trim();
          const locationAliasName = this.locationAlias().trim();
          const payload: CreateWalkoutRequest = {
            location_name: locationAliasName,
            location_id: location.id,
            walkout_date: walkoutDateIso,
            branch_name: branchAliasName,
            branch_id: branch.id,
            video_link: uploadedVideoLinks?.[0] ?? '',
            offset_seconds: videoOffset ?? 0,
            duration: videoDuration ?? 0,
            walkout_status: this.walkoutStatus().trim() || 'Review Theft',
            matching_transaction: !!this.matchingTransaction(),
            transaction_items: transItemsStr,
            // these will be deprecated and obsoleted over time
            pre_event_items: null,
            post_event_items: null,
            kiosk_uuid: locationAliasName,
            camera_uuid: this.cameraUuid() ?? '',
            theft_score: this.theftScore() ?? 1,
          };
          return this.walkoutService.createWalkout(payload);
        }),
        finalize(() => this.isCreatingWalkout.set(false)),
      )
      .subscribe({
        next: (response) => {
          const walkoutId = response.id;
          this.createdWalkoutId.set(walkoutId);
          this.createdReviewId.set(undefined);
          this.notify.notify('Walkout created successfully');
        },
        error: (err) => this.notify.notify(tryGetErrorMessage(err)),
      });
  }

  public createReview() {
    if (!this.canCreateReview() || this.isCreatingReview()) return;
    const locationId = this.locationId();
    if (!locationId) return;

    this.isCreatingReview.set(true);
    this.assignmentService
      .createAssignment(locationId, '', ReviewSubjectType.Walkout, true)
      .pipe(finalize(() => this.isCreatingReview.set(false)))
      .subscribe({
        next: (assignment) => {
          this.createdReviewId.set(assignment.review_id);
          this.notify.notify('Review created successfully');
          this.generateUuid();
        },
        error: (err) => this.notify.notify(tryGetErrorMessage(err)),
      });
  }

  private getSelectedPhotoFiles(): File[] {
    return this.photoRows()
      .map((r) => r.file)
      .filter((f): f is File => !!f);
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(String(e.target?.result ?? ''));
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  public onDateChange(date: Date) {
    if (!date) return;
    const next = new Date(date);
    const [hours, minutes, seconds] = this.parseTime(this.walkoutTime());
    next.setHours(hours, minutes, seconds ?? 0, 0);
    this.walkoutDateDate.set(next);
  }

  public onTimeChange(time: string) {
    if (!time) return;
    const next = new Date(this.walkoutDateDate());
    const [hours, minutes, seconds] = this.parseTime(time);
    next.setHours(hours, minutes, seconds ?? 0, 0);
    this.walkoutDateDate.set(next);
    this.walkoutTime.set(this.formatTime(next));
  }

  public setNow() {
    const now = new Date();
    this.walkoutDateDate.set(now);
    this.walkoutTime.set(this.formatTime(now));
  }

  public generateUuid() {
    const uuid = generateUuidUtil();
    this.uniqueId.set(uuid);
  }

  private formatTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  private parseTime(time: string): [number, number, number] {
    const parts = time.split(':').map((p) => parseInt(p, 10));
    const h = parts[0] || 0;
    const m = parts[1] || 0;
    const s = parts[2] || 0;
    return [h, m, s];
  }

  private serializeSimpleItemList(items: SimpleItem[]): string {
    const filtered = items.filter((i) => i.description && i.description.trim() !== '');
    return filtered.map((i) => i.description.trim()).join(', ');
  }

  ngOnInit(): void {
    this.generateUuid();
    this.photoRows.set([{ file: null, previewUrl: null }]);
    const query = this.route.snapshot.queryParams as Record<string, string>;
    const branchId = Number(query['branch_id']);
    const locationId = Number(query['location_id']);
    const branchAliasId = Number(query['branch_alias_id']);
    const locationAliasId = Number(query['location_alias_id']);
    this.branchIdParam.set(Number.isFinite(branchId) && branchId > 0 ? branchId : undefined);
    this.locationIdParam.set(Number.isFinite(locationId) && locationId > 0 ? locationId : undefined);
    this.branchAliasIdParam.set(Number.isFinite(branchAliasId) && branchAliasId > 0 ? branchAliasId : undefined);
    this.locationAliasIdParam.set(Number.isFinite(locationAliasId) && locationAliasId > 0 ? locationAliasId : undefined);
  }
}
